/**
 * Dead-family breakthrough — when refresh is terminally exhausted on all active
 * credential channels, the declaw stands down for the login panel only.
 * Does not alter token-refresh or silent-retry behavior; observes outcomes only.
 */

import { diagnosticLog } from '../utils/diagnosticLogger';
import {
  classifyRefreshFailure,
  parseRefreshErrorDetails,
  readLocalStorageRefreshFp,
  readAuthJsonFpSnapshot,
} from './authDiagnostics';
import {
  getElapsedSessionMs,
  hasConnectorStoredSession,
  hasStoredSession,
  shouldDeclawInvoluntaryLogout,
} from './authDeclaw';

export type AuthTerminalVerdict =
  | 'reauth_required'
  | 'family_revoked'
  | 'refresh_token_already_used';

type ChannelTerminal = {
  verdict: AuthTerminalVerdict;
  recordedAt: number;
};

let _localStorageTerminal: ChannelTerminal | null = null;
let _authJsonTerminal: ChannelTerminal | null = null;
let _silentRecoverySettled = false;
let _silentRecoverySucceeded = false;

function isTransientHttpFailure(status: unknown, data: unknown): boolean {
  const code = Number(status);
  if (code === 503 || code >= 500) return true;
  if (data && typeof data === 'object' && (data as Record<string, unknown>).transient) {
    return true;
  }
  return false;
}

function isNetworkOrTimeoutMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return lower.includes('network')
    || lower.includes('timeout')
    || lower.includes('aborted')
    || lower.includes('failed to fetch')
    || lower.includes('ipc transport probe timeout');
}

/** Map refresh HTTP / connector payloads to a terminal verdict, or null when transient. */
export function classifyTerminalAuthVerdict(
  status: unknown,
  data?: unknown,
  message?: string,
): AuthTerminalVerdict | null {
  let parsed = { errorCode: 'unknown', message: '' };
  try {
    parsed = parseRefreshErrorDetails(data);
  } catch { /* diagnostics optional */ }
  const msg = String(message ?? parsed.message ?? '');
  if (isNetworkOrTimeoutMessage(msg)) return null;
  if (isTransientHttpFailure(status, data)) return null;

  let classified: ReturnType<typeof classifyRefreshFailure> = 'transient';
  try {
    classified = classifyRefreshFailure(Number(status) || 0, data);
  } catch { /* diagnostics optional */ }
  if (classified === 'transient') return null;

  const msgLower = msg.toLowerCase();
  if (msgLower.includes('family_revoked') || msgLower.includes('auth_family_revoked')) {
    return 'family_revoked';
  }
  if (msgLower.includes('auth_reauth_required') || msgLower.includes('reauth_required')) {
    return 'reauth_required';
  }

  if (classified === 'already_used') {
    return 'refresh_token_already_used';
  }

  const code = Number(status);
  if (code === 401 || code === 403) {
    const { errorCode } = parseRefreshErrorDetails(data);
    if (errorCode.includes('already_used') || msgLower.includes('already used')) {
      return 'refresh_token_already_used';
    }
    return 'reauth_required';
  }

  return null;
}

export function noteLocalStorageRefreshTerminal(verdict: AuthTerminalVerdict): void {
  _localStorageTerminal = { verdict, recordedAt: Date.now() };
}

export function noteAuthJsonRefreshTerminal(verdict: AuthTerminalVerdict): void {
  _authJsonTerminal = { verdict, recordedAt: Date.now() };
}

export function noteAuthJsonRestoreFatalReason(reason: string): void {
  const map: Record<string, AuthTerminalVerdict | null> = {
    invalid_token: 'reauth_required',
    poisoned_refresh: 'reauth_required',
    already_used: 'refresh_token_already_used',
    refresh_rejected: 'reauth_required',
    exhausted: 'reauth_required',
    retry_exhausted: 'reauth_required',
  };
  const verdict = map[reason] ?? 'reauth_required';
  noteAuthJsonRefreshTerminal(verdict);
}

export function markSilentAuthRecoverySettled(succeeded: boolean): void {
  _silentRecoverySettled = true;
  _silentRecoverySucceeded = succeeded;
}

export function isSilentAuthRecoverySettled(): boolean {
  return _silentRecoverySettled;
}

/** True once silent recovery finished and both active channels are terminally dead. */
export function isAuthFamilyTerminalDead(): boolean {
  if (!_silentRecoverySettled || _silentRecoverySucceeded) return false;

  const lsActive = hasStoredSession();
  const jsonActive = hasConnectorStoredSession();

  if (lsActive && jsonActive) {
    return _localStorageTerminal !== null && _authJsonTerminal !== null;
  }
  if (jsonActive) return _authJsonTerminal !== null;
  if (lsActive) return _localStorageTerminal !== null;
  return _localStorageTerminal !== null || _authJsonTerminal !== null;
}

export function observeInvoluntaryLogoutTerminal(
  path: string,
  extras?: Record<string, unknown>,
): void {
  if (path === 'reauth_required_panel') {
    const payload = extras?.payload as { reason?: string } | undefined;
    const reason = String(payload?.reason ?? 'reauth_required');
    if (reason === 'family_revoked') {
      noteAuthJsonRefreshTerminal('family_revoked');
    } else if (reason === 'refresh_token_already_used') {
      noteAuthJsonRefreshTerminal('refresh_token_already_used');
    } else {
      noteAuthJsonRefreshTerminal('reauth_required');
    }
    return;
  }

  if (path === 'api_refresh_rejected') {
    const verdict = classifyTerminalAuthVerdict(extras?.status, extras?.data);
    if (verdict) noteLocalStorageRefreshTerminal(verdict);
  }
}

export async function shadowLogDeadFamilyBreakthrough(path: string): Promise<void> {
  try {
    const jsonSnap = await readAuthJsonFpSnapshot().catch(() => ({
      fp: 'read_failed',
      mtime: 'read_failed',
    }));
    diagnosticLog('warn', 'other', 'auth_declaw_dead_family_breakthrough', {
      kind: 'auth_declaw_dead_family_breakthrough',
      path,
      localStorage_fp: readLocalStorageRefreshFp(),
      auth_json_fp: jsonSnap.fp,
      auth_json_mtime: jsonSnap.mtime,
      elapsed_session_ms: getElapsedSessionMs(),
      localStorage_verdict: _localStorageTerminal?.verdict ?? null,
      auth_json_verdict: _authJsonTerminal?.verdict ?? null,
    });
  } catch { /* never propagate */ }
}

/** Gate helper: declaw may stand down for login when the family is truly dead. */
export function shouldBreakthroughDeadFamilyLogin(path: string): boolean {
  if (!shouldDeclawInvoluntaryLogout()) return false;
  if (!isAuthFamilyTerminalDead()) return false;
  void shadowLogDeadFamilyBreakthrough(path);
  return true;
}

export function resetAuthDeclawDeadFamilyForTests(): void {
  _localStorageTerminal = null;
  _authJsonTerminal = null;
  _silentRecoverySettled = false;
  _silentRecoverySucceeded = false;
}
